import Vue from "vue"
import api from "@/api"
import router from "@/router"
import { PaymentType } from "@reef-os/utils/enums/paymentType"
import { MenuType } from "@reef-os/utils/enums/menuType"
import { BusinessType } from "@reef-os/utils/enums/businessType"
import { UserNotificationType } from "@/enums/UserNotificationType"
import { UniqueProductLabelId } from "@/enums/UniqueProductLabelId"
import { BusinessEventType } from "@reef-os/utils/enums/businessEventType"
import { EventStatus } from "@reef-os/utils/enums/eventStatus"
import momentUtils from "@/moment-utils"
import moment from "moment"
import "moment-timezone"
import CartItemModel from "@/models/CartItemModel"
import { DeliveryMode } from "@reef-os/utils/enums/deliveryMode"

const vue = new Vue()

const errorLog = (err) => {
	return err
}

export default {
	async initHome({ commit, state, dispatch }, slug) {
		try {
			if (state.loading) {
				return
			}
			commit("loading")
			const route = router.app.$route
			const shouldRestoreOrderSession =
				route.name == "checkout" && route.params.order_session_id
			if (shouldRestoreOrderSession) {
				commit("clearOrder")
			}
			await dispatch("getBusiness", slug)
			await dispatch("getRestaurants")
			commit("calcMenuSchedules")
			dispatch("setResidentialOrderStartDate")
			dispatch("shouldShowModal", true)
			await Promise.all([
				dispatch("getMenu"),
				dispatch("getBusinessEvents")
			])
			const userToken = route.params.token || route.query.token
			const shouldValidateToken =
				state.business.type == BusinessType.RESIDENTIAL &&
				userToken != undefined
			const shouldGetVouchers =
				state.business.type == BusinessType.HOTEL ||
				state.business.type == BusinessType.COWORKING
			if (shouldValidateToken) {
				dispatch("authenticateUser", {
					token: userToken
				}).catch(() => {})
			}
			if (shouldRestoreOrderSession) {
				const restored = await dispatch(
					"restoreOrderSession",
					route.params.order_session_id
				)
				if (!restored) {
					router.push({ path: `/${slug}` })
				}
			}
			if (shouldGetVouchers) {
				await dispatch("getBusinessVoucherRequests", state.business.id)
			} else {
				commit("gotBusinessVoucherRequests", null)
			}

			dispatch("validateOrderData")
			commit("loaded")
			return true
		} catch (e) {
			commit("loaded")
			vue.bugsnag.notify("INIT_HOME failed", (event) => {
				event.addMetadata("error", {
					error: e
				})
			})
			return false
		}
	},
	async getBusinessEvents({ commit, state }) {
		let eventIds = ""
		try {
			if (state.business && state.business.menus) {
				eventIds = state.business.menus
					.filter((menu) => menu.event_id)
					.map((menu) => menu.event_id)
					.join(",")
				if (eventIds.length > 0) {
					const response = await api.getBusinessEvents(eventIds)
					commit("gotBusinessEvents", response.data.events)
				} else {
					commit("gotBusinessEvents", [])
				}
				return true
			}
			return false
		} catch (e) {
			vue.bugsnag.notify("Failed to fetch events", (event) => {
				event.addMetadata("error", {
					message: e.message ? e.message : e
				})
				event.addMetadata("data", {
					ids: eventIds
				})
			})
			return false
		}
	},
	async getBusinessEvent({ commit }, id) {
		try {
			const response = await api.getBusinessEvent(id)
			commit("gotBusinessEvents", [response.data.event])
			return true
		} catch (e) {
			vue.bugsnag.notify("Failed to fetch event", (event) => {
				event.addMetadata("error", {
					message: e.message ? e.message : e
				})
				event.addMetadata("data", {
					id: id
				})
			})
			return false
		}
	},
	async getBusiness({ commit }, slug) {
		await commit("gotBusiness", await api.getBusiness(slug))
	},
	async getBusinessVoucherRequests({ commit }, businessId) {
		const resp = await api.getBusinessVoucherRequests(businessId)
		if (resp.status == 200) {
			await commit(
				"gotBusinessVoucherRequests",
				resp.data.voucher_requests
			)
		}
	},
	async getMenu({ commit, state, dispatch, getters }) {
		try {
			commit("loadingMenu")
			const isEventRoute = router.app.$route.meta.isEvent === true
			const isEmbedRoute = router.app.$route.name === "embed"
			const isPreviewMode = router.app.$route.meta.preview == true
			const isMenuPreviewMode =
				router.app.$route.params.preview_menu != undefined
			const isInPreorderMode = state.deliveryDate != null
			const menuIds = []
			const backupMode =
				getters.inStockMenus.length > 0 &&
				getters.inStockMenus.every((menu) => menu.is_backup)

			let apis = state.business.menus
				.filter((menu) => {
					if (isMenuPreviewMode) {
						return menu.id == router.app.$route.params.preview_menu
					} else if (isPreviewMode) {
						return menu.type != MenuType.POPUP
					} else if (isEventRoute) {
						return menu.event_id == router.app.$route.params.eventId
					} else {
						if (isEmbedRoute) {
							return (
								menu.type != MenuType.POPUP && !menu.is_backup
							)
						} else if (isInPreorderMode) {
							const inStock =
								getters.inStockMenus.find(
									(inStockMenu) => menu.id == inStockMenu.id
								) != undefined
							return inStock
						} else {
							const isPrepackagedMenu =
								menu.type == MenuType.PRE_PACKAGED
							const inStock = menu.stock_status == 1
							const isValidMenu =
								(!backupMode && !menu.is_backup) ||
								(backupMode && menu.is_backup)
							return !isPrepackagedMenu && inStock && isValidMenu
						}
					}
				})
				.map((menu) => {
					menuIds.push(menu.id)
					let stockStatus = undefined
					if (isEmbedRoute || isPreviewMode) {
						stockStatus = undefined
					} else if (isInPreorderMode) {
						const todayDow = momentUtils.todaysDow()
						const tomorrowDow = todayDow == 6 ? 0 : 1
						const preorderDate = moment(
							state.deliveryDate.date,
							"YYYY-MM-DD"
						)
						const preorderDow =
							momentUtils.normalizeDayOfWeek(preorderDate)
						if (
							preorderDow == todayDow ||
							preorderDow == tomorrowDow
						) {
							const isLapsedMenu =
								Object.values(state.menuSchedules[menu.id])
									.flat()
									.filter(
										(schedule) =>
											schedule.rangesFrom == preorderDow
									).length > 1
							stockStatus =
								isLapsedMenu || preorderDow == todayDow
									? menu.stock_status
									: undefined
						}
					} else {
						stockStatus = menu.stock_status
					}
					return api
						.getMenuProducts(menu.id, stockStatus)
						.catch(errorLog)
				})

			const responses = await Promise.all(apis)

			commit("gotMenu", {
				responses: responses,
				ids: menuIds,
				inStockMenus: getters.inStockMenus,
				backupMode: backupMode
			})

			const promises = [
				dispatch("getCategories"),
				dispatch("getMenuBrands")
			]
			promises.forEach((promise) => promise.catch(errorLog))
			await Promise.all(promises)

			commit("loadedMenu")
		} catch (e) {
			commit("loadedMenu")
			throw e
		}
	},
	async getCategories({ commit, state }) {
		try {
			const categoryHash = state.menu
				.filter((product) => product.category_id != null)
				.reduce((categoryHash, product) => {
					if (
						categoryHash[product.category_id] == undefined &&
						state.categories[product.category_id] == undefined
					) {
						categoryHash[product.category_id] = ""
					}
					return categoryHash
				}, {})
			const ids = Object.keys(categoryHash)
			if (ids.length > 0) {
				const response = await api.getCategories(ids.join())
				if (response.status == 200 && response.data.categories) {
					response.data.categories.forEach((category) => {
						categoryHash[category.id] = category.name
					})
					commit("gotCategories", categoryHash)
					return true
				} else {
					throw `API Error ${response.status}`
				}
			} else {
				return true
			}
		} catch (e) {
			vue.bugsnag.notify("Failed to enrich categories", (event) => {
				event.addMetadata("error", {
					message: e.message ? e.message : e
				})
			})
			return false
		}
	},
	async getRestaurants({ commit, state }) {
		try {
			const ids = state.business.menus.reduce((ids, menu) => {
				if (!ids.includes(menu.restaurant_id)) {
					ids.push(menu.restaurant_id)
				}
				return ids
			}, [])
			if (ids.length > 0) {
				const response = await api.getRestaurants(ids.join(","))
				if (response.status == 200) {
					commit("gotRestaurants", response.restaurants)
				} else {
					throw `API Error: ${response.status}`
				}
			}
			return true
		} catch (e) {
			vue.bugsnag.notify("Failed to get restaurants", (event) => {
				event.addMetadata("error", {
					message: e.message ? e.message : e
				})
			})
			return false
		}
	},
	getItem({ commit, state }, payload) {
		const item = state.menu.find(
			(item) => item.id == payload.id && item.menu_id == payload.menuId
		)
		if (item) {
			commit("setItem", { product: item })
			return true
		} else {
			vue.bugsnag.notify("Tried to get an unavailable item", (event) => {
				event.addMetadata("payload", payload),
					event.addMetadata("items", state.menu)
			})
			return false
		}
	},
	processVoucherOrder({ dispatch, state }) {
		dispatch("sendMixpanelEvent", {
			msg: "Processing Voucher Order",
			data: {
				coupon: state.coupon
			}
		})
		dispatch("setInPurchaseState", true)
		dispatch("chargeCard", {
			nonce: state.coupon.token,
			wallet_type: ""
		})
	},
	processInRoomBillingOrder({ dispatch, state, getters }, payload) {
		dispatch("setInPurchaseState", true)
		dispatch("chargeCard", {
			nonce: "",
			wallet_type: "",
			payment_method: PaymentType.IN_ROOM_BILLING,
			reservation: {
				firstName: payload.alternateFirstName
					? payload.alternateFirstName
					: state.user.firstname,
				lastName: payload.alternateLastName
					? payload.alternateLastName
					: state.user.lastname,
				roomNumber:
					payload.alternateRoom && payload.alternateRoom.length > 0
						? payload.alternateRoom
						: getters.table
			}
		})
	},
	async chargeCard({ commit, state, getters }, payload) {
		let chargeCardData = {}
		try {
			if (getters.fullPreorderDate) {
				state.cart
					.filter((item) => !item.delivery_slot)
					.forEach((item) => {
						if (!item.delivery_time) {
							item.delivery_time = state.deliveryDate.time
							item.delivery_slot =
								getters.fullPreorderDate.format()
						}
					})
			}

			const paymentMethod =
				payload.wallet_type && payload.wallet_type !== "NONE"
					? payload.wallet_type
					: "CREDIT_CARD"
			const cartModel = state.cart.map((cartItem) =>
				CartItemModel.convertToChargeCardModel(cartItem)
			)

			chargeCardData = {
				business: {
					id: state.business.id,
					delivery_mode: getters.deliveryMode,
					menu_type: getters.cartMenuType,
					dropoff_location_id:
						getters.deliveryMode === 5 ? null : state.businessDropoffLocationId,
					is_accessible_pickup:
						state.is_accessible_pickup === null
							? 0
							: state.is_accessible_pickup
				},
				pricing: {
					full_price: getters.price,
					customer_price: parseFloat(getters.totalCheckoutPrice),
					tip: parseFloat(getters.totalTip),
					delivery_fee: getters.deliveryFee,
					coupon_id: state.coupon.id,
					coupon_type: state.coupon.couponType
				},
				transaction: {
					token: payload.nonce,
					idempotency_key: state.charge_key,
					payment_method: PaymentType[paymentMethod],
					wallet_type: payload.wallet_type
				},
				user: {
					name: state.user.username,
					email: state.user.email,
					phone: state.user.phone,
					location: getters.table
				},
				cart: cartModel
			}

			if (payload.payment_method != undefined) {
				chargeCardData.transaction.payment_method =
					payload.payment_method
			}

			if (state.user.id) {
				chargeCardData.user.user_id = state.user.id
			}

			if (payload.user_zip) {
				chargeCardData.user.zip = payload.user_zip
			}

			if (payload.save_card != undefined) {
				chargeCardData.transaction.save_card = payload.save_card
			}

			if (payload.card_id != undefined) {
				chargeCardData.transaction.card_id = payload.card_id
			}

			if (payload.reservation != undefined) {
				chargeCardData.business.reservation = {
					first_name: payload.reservation.firstName,
					last_name: payload.reservation.lastName,
					room_number: payload.reservation.roomNumber
				}
			}
			const resp = await api.createOrder(chargeCardData)
			if (resp.status == 200) {
				resp.paymentMethod = payload.paymentType
				commit("chargedCard", resp)
			} else {
				throw `API Error: ${resp.status}`
			}
			return true
		} catch (e) {
			 vue.bugsnag.notify(
					"Failed to charge card or create order",
					(event) => {
						event.addMetadata("exception", {
							error: e
						})
					}
				)
			if (e && e.code == "invalid_coupon_error") {
				commit("removeCoupon")
			}
			commit("chargeCardFailed", {
				error: e
			})
			return false
		}
	},
	async setChargeKey({ commit }, key) {
		commit("setChargeKey", key)
	},
	async calcDiscount({ commit, state, getters }, code) {
		try {
			const regex = /SMSOPTIN/i
			const isSMSCoupon = regex.test(code)
			if (isSMSCoupon && !state.user.smsOptIn) {
				return
			}
			const data = {
				business_id: state.business.id,
				price: getters.price,
				delivery_slot: getters.fullPreorderDate
			}
			if (state.user && state.user.id) {
				data.user_id = state.user.id
			}
			commit(
				"calcDiscount",
				await api.calcDiscount({
					code: code,
					data: data
				})
			)
		} catch (e) {
			commit("removeCoupon")
			vue.bugsnag.notify("Calc Discount Error", (event) => {
				event.addMetadata("error", {
					error: e
				})
				event.severity = "info"
			})
			if (e && e.code == "coupon_does_not_exist_error") {
				throw e.message ? e.message : `Coupon ${code} does not exist`
			} else if (e && e.code == "coupon_already_used_error") {
				throw e.message ? e.message : `Coupon ${code} was already used`
			} else if (e && e.code == "coupon_expired_error") {
				throw e.message ? e.message : `Coupon ${code} is expired`
			} else if (e && e.message) {
				throw e.message
			}

			throw e
		}
	},
	setCurrentItem({ commit }, payload) {
		commit("setCurrentItem", payload)
	},
	setInPurchaseState({ commit }, purchaseState) {
		commit("setInPurchaseState", purchaseState)
	},
	setCurrentItemOption({ commit }, payload) {
		commit("setItemOption", payload)
	},
	setCurrentItemComments({ commit }, payload) {
		commit("setCurrentItemComments", payload)
	},
	addToCart({ commit, state, getters, dispatch }) {
		let cartItem = null
		let suggestedCartItem = null
		const isResidential =
			state.business && state.business.type
				? state.business.type == BusinessType.RESIDENTIAL
				: false
		const isEventPage = router.app.$route.meta.isEvent === true
		let eventId = router.app.$route.params.eventId
			? router.app.$route.params.eventId
			: null
		const item = state.currentItem
		const menu = getters.menusHash[item.menu_id]
			? getters.menusHash[item.menu_id].menu
			: null
		if (
			!isEventPage &&
			!eventId &&
			menu &&
			menu.type == MenuType.POPUP &&
			menu.event_id
		) {
			eventId = menu.event_id
		}

		if (isEventPage || eventId) {
			const event = state.businessEvents.find(
				(event) => event.id == eventId
			)
			if (event) {
				let eventDate = null
				let eventTime = null
				if (event.type == BusinessEventType.POPUP) {
					if (event.status == EventStatus.PREORDER) {
						eventDate = event.start_date
						eventTime = event.start_time
					} else {
						eventDate = momentUtils.todaysDate()
					}
				} else {
					eventDate = event.end_date
					eventTime = event.end_time
				}
				cartItem = new CartItemModel(
					item,
					eventDate,
					eventTime,
					eventId
				)
				if (state.suggestedItemSelected.id) {
					suggestedCartItem = new CartItemModel(
						state.suggestedItemSelected,
						eventDate,
						eventTime,
						eventId
					)
				}
			} else {
				vue.bugsnag.notify(
					"Add to cart failed, unable to find event",
					(event) => {
						event.addMetadata("data", {
							item: item,
							cart: state.cart,
							eventId: eventId
						})
					}
				)
			}
		} else if (isResidential) {
			const oldItem = item.uuid
				? state.cart.find((cartItem) => cartItem.uuid === item.uuid)
				: null
			let deliveryTime = null
			let deliveryDate = state.deliveryDate.date
			if (oldItem) {
				deliveryDate = oldItem.delivery_date
				if (oldItem.delivery_time) {
					deliveryTime = oldItem.delivery_time
				}
			} else {
				const selectedDate = moment(deliveryDate, "YYYY-MM-DD")

				const pendingCartItemWithSlot = state.cart.find((cartItem) => {
					if (
						cartItem.delivery_slot != null &&
						item.menu_id == cartItem.menu_id
					) {
						const date = moment(
							cartItem.delivery_date,
							"YYYY-MM-DD"
						)
						return momentUtils.isSameDay(selectedDate, date)
					}
					return false
				})

				if (
					pendingCartItemWithSlot &&
					pendingCartItemWithSlot.delivery_time
				) {
					deliveryTime = pendingCartItemWithSlot.delivery_time
				} else {
					const selectedDow =
						momentUtils.normalizeDayOfWeek(selectedDate)
					const todaysDate = moment(
						momentUtils.todaysDate(),
						"YYYY-MM-DD"
					)
					const todaysTime = momentUtils.todaysTime()
					const isToday = momentUtils.isSameDay(
						selectedDate,
						todaysDate
					)
					const menu = getters.menusHash[item.menu_id].menu
					const inStock = menu.stock_status == 1
					let preferredSlots =
						menu.slots && menu.slots.length > 0 ? menu.slots : null
					const menuSchedule = state.menuSchedules[item.menu_id]
					const selectedDowSchedules =
						menuSchedule && menuSchedule[selectedDow]
							? menuSchedule[selectedDow]
							: []
					let timeSlots = momentUtils.generateTimeSlots(
						selectedDowSchedules,
						inStock,
						isToday
					)
					let availableTimes = timeSlots.map(
						(timeSlot) => timeSlot.time
					)
					if (preferredSlots && availableTimes) {
						preferredSlots = preferredSlots
							.slice()
							.sort(
								(a, b) =>
									momentUtils.numericalTime(a) -
									momentUtils.numericalTime(b)
							)
						deliveryTime = preferredSlots.find((preferredSlot) =>
							availableTimes.includes(preferredSlot)
						)
					}
					if (!deliveryTime) {
						deliveryTime =
							timeSlots[0] && timeSlots[0].time
								? timeSlots[0].time
								: null
					}
					if (
						isToday &&
						deliveryTime != -1 &&
						momentUtils.isBeforeTime(
							moment(deliveryTime, "HH:mm"),
							todaysTime
						)
					) {
						vue.bugsnag.notify(
							"Autoselected Invalid Delivery Slot",
							(event) => {
								event.addMetadata("error", {
									message: "Delivery slot in the past",
									slot: deliveryTime,
									dow: selectedDow,
									menuSchedule: menuSchedule
								})
							}
						)
						return
					}
				}
			}

			cartItem = new CartItemModel(
				item,
				deliveryDate,
				deliveryTime != -1 ? deliveryTime : null
			)
			if (state.suggestedItemSelected.id) {
				suggestedCartItem = new CartItemModel(
					state.suggestedItemSelected,
					deliveryDate,
					deliveryTime != -1 ? deliveryTime : null
				)
			}
		} else {
			cartItem = new CartItemModel(item)
			if (state.suggestedItemSelected.id) {
				suggestedCartItem = new CartItemModel(
					state.suggestedItemSelected
				)
			}
		}

		commit("addToCart", {
			cartItem: cartItem,
			suggestedCartItem: suggestedCartItem
		})

		dispatch("validateDeliveryMode")
	},
	removeItemFromCart({ commit, dispatch }, item) {
		commit("removeItemFromCart", item)
		dispatch("validateDeliveryMode")
	},
	removeCoupon({ commit }) {
		commit("removeCoupon")
	},
	setCurrentTipPer({ commit }, tipVal) {
		commit("setCurrentTipPer", tipVal)
	},
	setCurrentTipCustom({ commit }, tipVal) {
		commit("setCurrentTipCustom", tipVal)
	},
	setUsername({ commit }, username) {
		commit("setUsername", username.trim())
	},
	setUserFirstName({ commit }, username) {
		commit("setUserFirstName", username)
	},
	setUserLastName({ commit }, username) {
		commit("setUserLastName", username)
	},
	setUserEmail({ commit }, email) {
		commit("setUserEmail", email)
	},
	setUserPhone({ commit }, phone) {
		commit("setUserPhone", phone)
	},
	setDeliveryDate({ state, commit, dispatch }, payload) {
		if (!payload || !payload.preserveCart) {
			commit("clearCart")
		}
		commit("setDeliveryDate", payload)
		if (state.deliveryDate != null) {
			dispatch("sendMixpanelEvent", {
				msg: "Delivery Date Set",
				data: {
					preorder_date: state.deliveryDate
						? state.deliveryDate.date
						: null,
					preorder_time: state.deliveryDate
						? state.deliveryDate.time
						: null
				}
			})
		} else {
			dispatch("sendMixpanelEvent", { msg: "Delivery Date Cleared" })
		}
		dispatch("getMenu")
	},
	setResidentialDeliveryTime({ state, commit }, payload) {
		const uuids = state.cart
			.filter(
				(item) =>
					item.menu_id == payload.menuId &&
					item.delivery_date == payload.date
			)
			.map((item) => item.uuid)
		commit("setCartItemsDeliveryTime", {
			uuids: uuids,
			deliveryTime: payload.deliveryTime
		})
	},
	setCurrentTable({ commit }, table) {
		commit("setCurrentTable", table)
	},
	clearUserData({ commit }) {
		commit("clearUserData")
	},
	clearCart({ commit }) {
		commit("clearCart")
	},
	clearOrder({ commit }) {
		commit("clearOrder")
		commit("clearCart")
	},
	updateItemCount({ commit }, payload) {
		if (payload.quantity > 0) {
			commit("updateItemCount", payload)
		}
	},
	updateCurrentItemQuantity({ commit }, quantity) {
		if (quantity > 0) {
			commit("updateCurrentItemQuantity", quantity)
		}
	},
	async editItem({ commit }, item) {
		commit("setEditableItem", item)
	},
	selectSuggestedItem({ commit }, item) {
		commit("selectSuggestedItem", item)
	},
	async restoreOrderSession({ commit, state, dispatch }, orderSessionId) {
		try {
			const sessionResponse = await api.getOrderSession(orderSessionId)
			if (sessionResponse.status != 200) {
				throw "Order Session api failed"
			}

			commit("clearUserData")
			commit("gotOrderSession", sessionResponse.data.order_session)
			dispatch("setChargeKey")
			dispatch("sendMixpanelEvent", {
				msg: "Order Session Restored",
				data: {
					orderSessionId: orderSessionId
				}
			})
			if (state.coupon.id) {
				const couponResponse = await api.getCoupon(state.coupon.id)
				if (couponResponse.status == 200 && couponResponse.data) {
					await dispatch("calcDiscount", couponResponse.data.code)
				}
			}
			dispatch("validateDeliveryMode")
			return true
		} catch (e) {
			console.warn(e)
			vue.bugsnag.notify("Failed to get order session", (event) => {
				event.addMetadata("error", {
					message: e.message ? e.message : e
				})
			})
			return false
		}
	},
	async getOrderStatus({ commit, state, dispatch }, payload) {
		try {
			const response = await api.getOrderStatus(payload.orderId)
			commit("gotOrderStatus", response)
			const delivery = {
				status: 0,
				deliveredDate: ""
			}
			const deliveryJobId = payload.deliveryJobId
				? payload.deliveryJobId
				: state.orderStatus.sub_orders[0].delivery_job_id
			if (deliveryJobId !== undefined && deliveryJobId != "") {
				await dispatch("getDeliveryStatusJob", deliveryJobId)
				await dispatch("getDelivery", deliveryJobId)
			} else {
				commit("resetDeliveryStatus", delivery)
			}
			return true
		} catch (e) {
			if (!e || e.message != "Network Error") {
				vue.bugsnag.notify("Failed to fetch order status", (event) => {
					event.addMetadata("error", {
						message: e.message ? e.message : e
					})
				})
			}
			return false
		}
	},
	async getReceipt({ commit }, payload) {
		try {
			const response = await api.getReceipt(payload.orderId)
			commit("gotReceipt", response)
			return true
		} catch (e) {
			if (!e || e.message != "Network Error") {
				vue.bugsnag.notify("Failed to fetch receipt", (event) => {
					event.addMetadata("error", {
						message: e.message ? e.message : e
					})
				})
			}
			return false
		}
	},
	async getDeliveryStatusJob({ commit }, id) {
		try {
			const promises = [api.getDelivery(id)]
			const responses = await Promise.all(promises)
			if (responses[0] && responses[0].data) {
				commit("gotDeliveryStatusId", responses[0].data)
			}
			return true
		} catch (e) {
			vue.bugsnag.notify("Failed to get delivery status job", (event) => {
				event.addMetadata("error", {
					message: e.message ? e.message : e
				})
			})
			return false
		}
	},
	async getDelivery({ commit, state }, id) {
		try {
			const promises = [api.getDeliveryStatus(id)]
			if (
				state.orderStatus &&
				state.orderStatus.delivery_mode == DeliveryMode.LOCKER
			) {
				promises.push(api.getDelivery(id))
			}

			promises.forEach((promise) =>
				promise.catch((e, i) => {
					vue.bugsnag.notify(
						"Failed to get delivery api",
						(event) => {
							event.addMetadata("error", {
								message: e.message ? e.message : e,
								promiseIndex: i
							})
						}
					)
				})
			)

			const responses = await Promise.all(promises)
			if (responses[0] && responses[0].data) {
				commit("gotDeliveryStatus", responses[0].data)
			}
			if (responses[1] && responses[1].data) {
				commit("gotDelivery", responses[1].data)
			}
			return true
		} catch (e) {
			vue.bugsnag.notify("Failed to get delivery status", (event) => {
				event.addMetadata("error", {
					message: e.message ? e.message : e
				})
			})
			return false
		}
	},
	async updateOrderStatus({ dispatch }, payload) {
		try {
			await api.updateOrderStatus(payload.id, payload.status, {
				force_delivery: payload.markDelivered ? true : false
			})
			await dispatch("getOrderStatus", {
				orderId: payload.orderId
			})
			return true
		} catch (e) {
			console.warn(e)
			vue.bugsnag.notify("Failed to get update order status", (event) => {
				event.addMetadata("error", {
					message: e.message ? e.message : e
				})
			})
			return false
		}
	},
	sendMixpanelEvent({ state, getters }, event) {
		const data = event.data ? event.data : {}

		const payload = Object.assign(data, {
			is_kiosk: false,
			business: data.slug ? data.slug : state.business.slug
		})

		if (state.business && state.business.type != null) {
			payload.business_type = state.business.type
		}
		if (state.business && getters.menuType != null) {
			payload.menu_type = getters.menuType
		}

		if (state.user && state.user.id != 0) {
			payload.user_id = state.user.id
		}

		if (state.temporaryFlag.isGuestCheckoutActive) {
			payload.is_guest = true
		}

		mixpanel.track(event.msg, payload, (success, payload) => {
			if (success == 0) {
				vue.bugsnag.notify("Mixpanel Failed to send", (event) => {
					event.addMetadata("data", {
						event: payload && payload.event ? payload.event : "N/A",
						properties:
							payload && payload.properties
								? JSON.stringify(payload.properties)
								: "N/A",
						trackingBlocked:
							navigator && navigator.doNotTrack == "1"
								? true
								: false
					})
				})
			}
		})
		vue.bugsnag.leaveBreadcrumb("Mixpanel Event", {
			event: event.msg,
			data: payload
		})
	},
	sendForkLiftEvent({ state }, eventName) {
		api.sendEvent({
			name: eventName,
			attributes: {
				business_id: state.business.id
			}
		}).catch(() => {})
	},
	validateOrderData({ commit, state, dispatch, getters }) {
		//Clear stale preorders for non residentials
		if (
			state.business.type != BusinessType.RESIDENTIAL &&
			state.deliveryDate != null
		) {
			const deliveryDate = moment(
				`${state.deliveryDate.date} ${state.deliveryDate.time}`,
				"YYYY-MM-DD HH:mm:ss"
			).subtract(30, "m")
			if (moment().isAfter(deliveryDate)) {
				dispatch("setDeliveryDate", null)
			}
		}

		if (state.cart.length > 0) {
			let itemsToRemove = []
			let itemUUIDsToRemove = []

			//Menu no longer exists
			state.cart.forEach((item) => {
				if (
					!getters.menusHash[item.menu_id] &&
					!itemUUIDsToRemove.includes(item.uuid)
				) {
					itemsToRemove.push(item)
					itemUUIDsToRemove.push(item.uuid)
				}
			})

			//Ensure no 2Go or Prepackaged carts
			state.cart
				.filter((item) => {
					return (
						item.menu_type == MenuType.ECOMMERCE ||
						item.menu_type == MenuType.DONATION ||
						item.menu_type == MenuType.PRE_PACKAGED
					)
				})
				.forEach((item) => {
					if (!itemUUIDsToRemove.includes(item.uuid)) {
						itemsToRemove.push(item)
						itemUUIDsToRemove.push(item.uuid)
					}
				})

			//Ensure no stale carts
			if (state.business.type == BusinessType.RESIDENTIAL) {
				const todaysTime = momentUtils.todaysTime()
				const todaysTimeMoment = moment.utc(todaysTime, "HH:mm")
				const todaysDate = momentUtils.todaysDate()
				const todaysFullDate = moment()
				state.cart.forEach((item) => {
					const itemDeliveryDate = moment(
						item.delivery_date,
						"YYYY-MM-DD"
					)
					const itemDeliveryTime =
						item.delivery_time && item.delivery_time != -1
							? moment.utc(item.delivery_time, "HH:mm")
							: null
					let deliveryDateStale =
						item.delivery_date &&
						momentUtils.isBeforeDay(itemDeliveryDate, todaysDate)
					let deliveryTimeStale = false
					if (!deliveryDateStale && itemDeliveryTime) {
						deliveryTimeStale = moment(
							`${item.delivery_date} ${item.delivery_time}`
						).isBefore(todaysFullDate)
					}
					const menuHasSchedule = item.menu_type != MenuType.POPUP
					if (
						!deliveryDateStale &&
						!deliveryTimeStale &&
						menuHasSchedule
					) {
						const schedule = state.menuSchedules[item.menu_id]
						const dow =
							momentUtils.normalizeDayOfWeek(itemDeliveryDate)
						if (
							!schedule ||
							!schedule[dow] ||
							schedule[dow].length == 0
						) {
							deliveryDateStale = true
						} else {
							let deliveryTime = itemDeliveryTime
								? itemDeliveryTime
								: todaysTimeMoment
							let foundMatchingSchedule = schedule[dow].some(
								(day) => {
									if (day.rangesFrom == dow) {
										return momentUtils.isBetweenTimes(
											deliveryTime,
											day.openTime,
											day.closeTime
										)
									}
								}
							)
							deliveryTimeStale = !foundMatchingSchedule
						}
					}

					if (
						(deliveryDateStale || deliveryTimeStale) &&
						!itemUUIDsToRemove.includes(item.uuid)
					) {
						itemsToRemove.push(item)
						itemUUIDsToRemove.push(item.uuid)
					}
				})
			} else if (state.lastCartUpdate) {
				const maxCartAge = moment(state.lastCartUpdate).add(48, "hours")
				const now = moment()
				if (now.isAfter(maxCartAge)) {
					itemsToRemove = []
					itemUUIDsToRemove = []
					commit("clearCart")
				}
			}

			//Ensure items are valid
			state.cart
				.filter((item) => !itemUUIDsToRemove.includes(item.uuid))
				.forEach((item) => {
					//Filter out based on labels
					let hasPreorderOnlyLabel =
						item.labels &&
						item.labels.some(
							(label) =>
								label.id == UniqueProductLabelId.PREORDER_ONLY
						)
					let isPopupMenu = item.menu_type == MenuType.POPUP
					if (hasPreorderOnlyLabel && isPopupMenu) {
						let menu = getters.menusHash[item.menu_id]
							? getters.menusHash[item.menu_id].menu
							: null
						let eventId =
							menu && menu.event_id ? menu.event_id : null
						if (eventId) {
							let event = state.businessEvents.find(
								(event) => event.id == eventId
							)
							if (event && event.status == EventStatus.ONGOING) {
								itemsToRemove.push(item)
								itemUUIDsToRemove.push(item.uuid)
							}
						}
					}
				})

			if (itemsToRemove.length > 0) {
				itemsToRemove.forEach((item) => {
					dispatch("removeItemFromCart", item)
				})
			}

			if (state.cart.length == 0 && state.coupon.id != 0) {
				dispatch("removeCoupon")
			}
		}

		dispatch("validateDeliveryMode")
	},
	setResidentialOrderStartDate({ commit, state, getters }) {
		const isActive =
			state.business.is_active == 1 ||
			router.app.$route.meta.preview == true
		if (
			state.business.type == BusinessType.RESIDENTIAL &&
			getters.residentialMenuStartDate &&
			isActive
		) {
			if (
				!state.deliveryDate ||
				!state.deliveryDate.date ||
				(state.deliveryDate &&
					state.deliveryDate.date &&
					momentUtils.isBeforeDay(
						moment(state.deliveryDate.date),
						getters.residentialMenuStartDate
					))
			) {
				commit("setDeliveryDate", {
					date: getters.residentialMenuStartDate
				})
			} else if (!getters.residentialMenuStartDate) {
				vue.bugsnag.notify("Residential order start date unavailable")
			}
		}
	},
	setPreorderOnlyDeliverySlot({ dispatch, state, getters }) {
		if (getters.preorderOnly && getters.preorderOnlySlots) {
			const timezone =
				state.business && state.business.timezone
					? state.business.timezone
					: ""
			if (getters.fullPreorderDate) {
				const localFullPreorderDate = getters.fullPreorderDate
					.clone()
					.tz(timezone)
				const preorderDate = localFullPreorderDate.format("YYYY-MM-DD")
				const preorderTime = localFullPreorderDate.format("HH:mm:ss")
				const preorderOnlyDate = getters.preorderOnlySlots[preorderDate]
				if (
					preorderOnlyDate &&
					preorderOnlyDate.slots.includes(preorderTime)
				) {
					return
				}
			}

			const todaysDate = moment.tz(timezone).startOf("d")
			const workingDate = todaysDate.clone()
			let slot = null
			for (var i = 0; i < 5; i++) {
				let workingDateFormatted = workingDate.format("YYYY-MM-DD")
				let preorderDate =
					getters.preorderOnlySlots[workingDateFormatted]
				if (
					preorderDate &&
					preorderDate.slots &&
					preorderDate.slots.length > 0
				) {
					slot = {
						date: workingDateFormatted,
						time: preorderDate.slots[0]
					}
					break
				} else {
					workingDate.add(1, "d")
				}
			}

			if (slot) {
				dispatch("setDeliveryDate", {
					date: slot.date,
					time: slot.time,
					preserveCart: false
				})
			}
		}
	},
	async authenticateUser({ commit, state, dispatch }, options) {
		try {
			let response = null
			if (options.phone) {
				response = await api.authenticateUserViaSMS({
					phone_number: options.phone,
					business_id: state.business.id
				})
			} else if (options.token) {
				response = await api.authenticateUserViaToken({
					token: options.token
				})
			} else if (options.facebookCode) {
				response = await api.authenticateUserViaFacebook({
					code: options.facebookCode,
					business_id: state.business.id
				})
			} else if (options.googleCode) {
				const params = {
					code: options.googleCode,
					business_id: state.business.id
				}
				if (process.env.NODE_ENV == "development") {
					params.redirect_uri =
						"http://localhost:8080/auth/google/callback.html"
				}
				response = await api.authenticateUserViaGoogle(params)
			}
			if (response) {
				if (response.status == 200 && response.data.access_token) {
					commit("userAuthenticated", response.data)
					await dispatch("getUser")
					if (state.user.isVerified) {
						dispatch("getUserCoupons")
						dispatch("getUserCampaigns")
					}
				} else {
					throw `API Error ${response.status}`
				}
				return true
			} else {
				throw "Invalid Auth Method"
			}
		} catch (e) {
			vue.bugsnag.notify("Failed to authenticate user", (event) => {
				event.addMetadata("error", {
					error: e,
					options: options
				})
			})
			throw e
		}
	},
	async verifyUser({ commit, state }, code) {
		try {
			const response = await api.verifyUser(state.user.id, {
				code: code
			})
			if (response.status == 200 && response.data.user) {
				commit("updatedUser", response.data)
				return state.user.isVerified
			} else {
				throw `API Error ${response.status}`
			}
		} catch (e) {
			vue.bugsnag.notify("Failed to verify user", (event) => {
				event.addMetadata("error", {
					error: e,
					code: code
				})
			})
			throw e.message ? e.message : e
		}
	},
	async sendSMSValidation({ state }, phone) {
		try {
			await api.sendSMSVerificationCode(state.user.id, {
				phone_number: phone
			})
			return true
		} catch (e) {
			if (e && e.status && e.status == 400) {
				vue.bugsnag.notify("Failed to verify user", (event) => {
					event.addMetadata("error", {
						error: e,
						phone: phone
					})
				})
			} else {
				vue.bugsnag.notify("Failed to verify user", (event) => {
					event.addMetadata("error", {
						error: e,
						phone: phone
					})
					event.severity = "error"
				})
			}
			throw e
		}
	},
	async activateUser({ commit, state, dispatch }, payload) {
		try {
			const response = await api.activateUser(state.user.id, payload)
			if (response.status == 200) {
				commit("updatedUser", response.data)
				dispatch("getUserCoupons")
				dispatch("getUserCampaigns")
			} else {
				throw `API Error: ${response.status}`
			}
			return true
		} catch (e) {
			vue.bugsnag.notify("Failed to activate user", (event) => {
				event.addMetadata("error", e)
				event.addMetadata("data", payload)
			})
			return e
		}
	},
	async updateUser({ commit, state }, payload) {
		try {
			const data = {
				user: {}
			}
			if (payload.username) {
				data.user.first_name = payload.username
			}
			if (payload.email) {
				data.user.email = payload.email
			}
			if (payload.apartmentNumber) {
				data.user.apartment_number = payload.apartmentNumber
			}
			if (payload.phone) {
				data.user.phone_number = payload.phone
			}
			if (payload.smsOptIn != undefined) {
				data.user.sms_opt_in = payload.smsOptIn
			}
			const response = await api.updateUser(state.user.id, data)
			if (response.status == 200 && response.data.user) {
				commit("updatedUser", response.data)
				return state.user.isVerified
			} else {
				throw `API Error ${response.status}`
			}
		} catch (e) {
			const metadata = Object.assign(payload, {
				error: e,
				userId: state.user.id
			})
			vue.bugsnag.notify("Failed to update user details", (event) => {
				event.addMetadata("error", metadata)
			})
			throw e
		}
	},
	async updateUserNotification({ commit, state }, payload) {
		try {
			let response = null
			if (payload.optIn) {
				response = await api.addUserNotification(state.user.id, {
					type: payload.type
				})
			} else {
				const notification = state.user.notifications.find(
					(notification) =>
						notification.type ==
						UserNotificationType.UPCOMING_EVENT_SMS
				)
				if (notification) {
					response = await api.removeUserNotification(notification.id)
				}
			}
			if (response && response.status != 200) {
				throw `API Error: ${response.status}`
			}
			commit("updateUserNotification", {
				type: payload.type,
				notification: response.data ? response.data.notification : null
			})
			return true
		} catch (e) {
			vue.bugsnag.notify(
				"Failed to update user notification",
				(event) => {
					event.addMetadata("error", {
						error: e,
						userId: state.user.id,
						optIn: payload.optIn,
						type: payload.type
					})
				}
			)
			return false
		}
	},
	async getUser({ commit, state }) {
		try {
			const response = await api.getUser(state.user.accessToken)
			if (response.status == 200 && response.data.user) {
				commit("updatedUser", response.data)
				return true
			} else {
				throw `API Error ${response.status}`
			}
		} catch (e) {
			vue.bugsnag.notify("Failed to fetch user", (event) => {
				event.addMetadata("error", {
					error: e
				})
				event.addMetadata("userData", state.user)
			})
			if (e && e.code) {
				return {
					error: e.code
				}
			}
			throw e
		}
	},
	async getUserCoupons({ commit, state }) {
		try {
			const response = await api.getUserCoupons(state.user.id)
			if (response.status == 200 && response.data.coupons) {
				commit("gotUserCoupons", response.data.coupons)
				return true
			} else {
				throw `API Error ${response.status}`
			}
		} catch (e) {
			console.warn(e)
		}
	},
	async getUserCampaigns({ commit, state }) {
		try {
			const response = await api.getUserCampaigns(state.user.id)
			if (response.status == 200 && response.data.campaigns_progress) {
				commit("gotUserCampaigns", response.data.campaigns_progress)
				return true
			} else {
				throw `API Error ${response.status}`
			}
		} catch (e) {
			console.warn(e)
		}
	},
	async getUserPaymentMethods({ commit, state }) {
		try {
			if (state.user.id && state.user.id != 0) {
				const response = await api.getUserPaymentMethods(state.user.id)
				if (response.status == 200 && response.data.payment_methods) {
					commit(
						"gotUserPaymentMethods",
						response.data.payment_methods
					)
				} else {
					throw `API Error ${response.status}`
				}
			}
		} catch (e) {
			vue.bugsnag.notify(
				"Failed to get user payment methods",
				(event) => {
					event.addMetadata("error", {
						error: e,
						userId: state.user.id
					})
				}
			)
			throw e
		}
	},
	async getUserOrders({ commit, state, dispatch }) {
		try {
			if (state.user.id && state.user.id != 0) {
				let orders = []
				let getUserOrdersPromise = api.getUserOrders({
					filters: {
						user: {
							ids: [state.user.id]
						}
					},
					sorting: [
						{
							sort_by: "created_time",
							sort_order: "desc"
						}
					]
				})
				const promises = [
					getUserOrdersPromise,
					dispatch("getBusiness", state.user.businessId)
				]
				const responses = await Promise.all(promises)
				dispatch("getRestaurants").then(() => {
					commit("calcMenuSchedules")
				})
				const ordersResponse = responses[0]

				if (
					ordersResponse.status == 200 &&
					ordersResponse.data.orders
				) {
					orders = ordersResponse.data.orders.filter((order) => {
						const hasSuborders =
							order.sub_orders && order.sub_orders.length > 0
						const isUserBusiness =
							order.business.id == state.user.businessId
						return hasSuborders && isUserBusiness
					})

					const menuNames = state.business.menus.reduce(
						(menuNames, menu) => {
							menuNames[menu.id] = menu.name
							return menuNames
						},
						{}
					)

					const missingMenuIds = orders.reduce((menuIds, order) => {
						let menuId = order.sub_orders[0].products[0].menu_id
						if (!menuIds.includes(menuId) && !menuNames[menuId]) {
							menuIds.push(menuId)
						}
						return menuIds
					}, [])

					let secondaryPromises = [
						dispatch("validateOrdersReorder", orders)
					]
					if (missingMenuIds.length > 0) {
						secondaryPromises.push(
							api
								.getRestaurantMenus(missingMenuIds.join(","))
								.catch(() => {})
						)
					}

					const secondaryResponses = await Promise.all(
						secondaryPromises
					)

					const validOrdersForReordering = secondaryResponses[0]
					const menusResponse = secondaryResponses[1]

					if (menusResponse && menusResponse.status == 200) {
						menusResponse.data.menus.forEach((menu) => {
							if (!menuNames[menu.id]) {
								menuNames[menu.id] = menu.name
							}
						})
					}

					orders.forEach((order) => {
						let menuId = order.sub_orders[0].products[0].menu_id
						order.menuName = menuNames[menuId]
							? menuNames[menuId]
							: "N/A"
						order.canReorder = validOrdersForReordering.includes(
							order.id
						)
					})

					commit("gotUserOrders", orders)
				} else {
					throw `Orders API Error ${ordersResponse.status}`
				}
			}
		} catch (e) {
			commit("gotUserOrders", [])
			vue.bugsnag.notify("Failed to get user orders", (event) => {
				event.addMetadata("error", {
					error: e,
					userId: state.user.id
				})
			})
			throw e
		}
	},
	async validateOrdersReorder({ state }, orders) {
		try {
			const menus = {}
			let validReorders = []

			state.business.menus
				.filter(
					(menu) =>
						menu.opening_hours && menu.opening_hours.length > 0
				)
				.forEach((menu) => {
					let products = []
					menu.products.forEach((product) => {
						products.push(product.id)
					})
					menus[menu.id] = {
						products: products
					}
				})

			const pendingMetadataValidation = {
				products: [],
				orders: {}
			}

			orders.forEach((order) => {
				const suborder = order.sub_orders[0]
				const isOngoing =
					suborder.delivered_time == undefined ||
					suborder.delivered_time == null
				if (suborder.event || isOngoing) {
					return false
				}
				for (let i = 0; i < suborder.products.length; i++) {
					let product = suborder.products[i]
					let menu = menus[product.menu_id]
					if (menu && menu.products.includes(product.id)) {
						validReorders.push(order.id)
						if (product.metas && product.metas.length > 0) {
							if (
								!pendingMetadataValidation.products.includes(
									product.id
								)
							) {
								pendingMetadataValidation.products.push(
									product.id
								)
							}
							if (!pendingMetadataValidation.orders[order.id]) {
								pendingMetadataValidation.orders[order.id] = {
									metas: [],
									orderId: order.id
								}
							}
							product.metas.forEach((meta) => {
								if (
									!pendingMetadataValidation.orders[order.id]
										.metas[meta.meta_id]
								) {
									pendingMetadataValidation.orders[
										order.id
									].metas[meta.meta_id] = []
								}
								pendingMetadataValidation.orders[
									order.id
								].metas[meta.meta_id].push(meta)
							})
						}
					} else {
						break
					}
				}
			})

			if (pendingMetadataValidation.products.length > 0) {
				let productIds = pendingMetadataValidation.products.join(",")
				const productsResponse = await api.getProductsByIds(productIds)
				if (productsResponse.status == 200) {
					const existingMetas = {}
					productsResponse.data.products.forEach((product) => {
						product.metas.forEach((meta) => {
							existingMetas[meta.id] = {
								metaValueIds: meta.values.map(
									(metaValue) => metaValue.id
								),
								maximum: meta.max_allowed_values,
								minimum: meta.min_required_values
							}
						})
					})
					Object.values(pendingMetadataValidation.orders).forEach(
						(order) => {
							let hasValidMetas = Object.keys(order.metas).every(
								(metaId) => {
									let existingMeta = existingMetas[metaId]
									if (existingMeta) {
										let metaValues = order.metas[metaId]
										let metaValuesCount = metaValues.length
										let exceedsMaximum =
											existingMeta.maximum != 0 &&
											metaValuesCount >
												existingMeta.maximum
										let belowMinimum =
											existingMeta.minimum != 0 &&
											metaValuesCount <
												existingMeta.minimum
										return (
											!exceedsMaximum &&
											!belowMinimum &&
											metaValues.every((metaValue) =>
												existingMeta.metaValueIds.includes(
													metaValue.meta_val_id
												)
											)
										)
									}
									return false
								}
							)
							if (!hasValidMetas) {
								validReorders = validReorders.filter(
									(orderId) => orderId != order.orderId
								)
							}
						}
					)
				} else {
					let orderIds = Object.keys(pendingMetadataValidation.orders)
					validReorders = validReorders.filter(
						(orderId) => !orderIds.includes(orderId)
					)
				}
			}

			return validReorders
		} catch (e) {
			vue.bugsnag.notify(
				"Failed to validate user orders for reordering",
				(event) => {
					event.addMetadata("error", {
						error: e
					})
				}
			)
			return []
		}
	},
	async updateOrderReorder({ commit }, payload) {
		commit("updateOrderReorder", payload)
	},
	async reorderOrder({ state, commit }, order) {
		try {
			const menus = {}
			const deliverySlots = {}
			state.business.menus.forEach((menu) => {
				menus[menu.id] = menu
			})

			const suborder = order.sub_orders[0]
			const productIds = suborder.products
				.map((product) => product.id)
				.join(",")
			const response = await api.getProductsByIds(productIds)
			if (response.status == 200) {
				const restaurantProducts = {}
				const items = []
				response.data.products.forEach((product) => {
					restaurantProducts[product.id] = product
				})

				const todaysDow = momentUtils.todaysDow()
				suborder.products.forEach((product) => {
					let menu = menus[product.menu_id]
					let menuInStock = menu.stock_status == 1
					let restaurantProduct = restaurantProducts[product.id]
					let restaurantProductInStock =
						restaurantProduct.stock_status == 1
					let metasInStock = true
					if (product.metas && product.metas.length > 0) {
						let restaurantProductMetas = {}
						restaurantProduct.metas.forEach((meta) => {
							restaurantProductMetas[meta.id] = {}
							meta.values.forEach((metaValue) => {
								restaurantProductMetas[meta.id][metaValue.id] =
									metaValue.stock_status
							})
						})
						metasInStock = product.metas.every((meta) => {
							return (
								restaurantProductMetas[meta.meta_id][
									meta.meta_val_id
								] == 1
							)
						})
					}
					let asapAvailable =
						menuInStock && restaurantProductInStock && metasInStock
					if (asapAvailable && !deliverySlots[menu.id]) {
						deliverySlots[menu.id] = {
							date: momentUtils.todaysDate(),
							time: -1
						}
					} else if (
						!asapAvailable &&
						(!deliverySlots[menu.id] ||
							deliverySlots[menu.id] == -1)
					) {
						let preferredSlots =
							menu.slots && menu.slots.length > 0
								? menu.slots
								: null
						const menuSchedule = state.menuSchedules[menu.id]
						const isLapsedMenu =
							Object.values(menuSchedule)
								.flat()
								.filter(
									(schedule) =>
										schedule.rangesFrom == todaysDow
								).length > 1
						let availableDow = momentUtils.incrementDow(todaysDow)
						if (isLapsedMenu) {
							availableDow =
								momentUtils.incrementDow(availableDow)
						}
						for (let i = availableDow - todaysDow; i < 6; i++) {
							if (menuSchedule[availableDow].length > 0) {
								const selectedDowSchedules =
									menuSchedule && menuSchedule[availableDow]
										? menuSchedule[availableDow]
										: []
								let timeSlots = momentUtils.generateTimeSlots(
									selectedDowSchedules,
									menuInStock,
									false
								)
								let availableTimes = timeSlots.map(
									(timeSlot) => timeSlot.time
								)
								if (
									availableTimes &&
									availableTimes.length > 0
								) {
									let preferredTime = null
									if (preferredSlots) {
										preferredSlots = preferredSlots
											.slice()
											.sort(
												(a, b) =>
													momentUtils.numericalTime(
														a
													) -
													momentUtils.numericalTime(b)
											)
										preferredSlots.find((preferredSlot) =>
											availableTimes.includes(
												preferredSlot
											)
										)
									}
									deliverySlots[menu.id] = {
										date: moment()
											.add(i, "days")
											.format("YYYY-MM-DD"),
										time: preferredTime
											? preferredTime
											: availableTimes[0]
									}
									break
								}
							}
							availableDow =
								momentUtils.incrementDow(availableDow)
						}
					}
				})

				suborder.products.forEach((product) => {
					let menu = menus[product.menu_id]
					let restaurantProduct = restaurantProducts[product.id]
					let restaurantProductMetas = {}
					let businessProduct = menu.products.find(
						(businessProduct) => businessProduct.id == product.id
					)
					let productPrice =
						businessProduct && businessProduct.price
							? businessProduct.price
							: restaurantProduct.price
					let productName =
						businessProduct && businessProduct.name
							? businessProduct.name
							: restaurantProduct.name
					let options = {}

					if (restaurantProduct.metas) {
						restaurantProduct.metas.forEach((meta) => {
							let metaValues = {}
							meta.values.forEach((metaValue) => {
								metaValues[metaValue.id] = {
									id: metaValue.id,
									meta_id: metaValue.meta_id,
									price: metaValue.price
								}
							})
							restaurantProductMetas[meta.id] = {
								id: meta.id,
								required: meta.required,
								values: metaValues
							}
						})
						const requiredMetaIds = Object.values(
							restaurantProductMetas
						)
							.filter((meta) => meta.required)
							.map((meta) => meta.id)

						const productMetaIds = product.metas
							? product.metas.map((meta) => meta.meta_id)
							: []
						const hasRequiredMetaIds = requiredMetaIds.every(
							(metaId) => productMetaIds.includes(metaId)
						)
						if (!hasRequiredMetaIds) {
							throw "Missing Required Metadata"
						}
					}

					if (product.metas) {
						product.metas.forEach((meta) => {
							if (!options[meta.key]) {
								options[meta.key] = []
							}
							let restaurantProductMeta =
								restaurantProductMetas[meta.meta_id]
							let restaurantProductMetaValue =
								restaurantProductMeta.values[meta.meta_val_id]
							options[meta.key].push({
								checked: true,
								id: meta.meta_val_id,
								meta_id: meta.meta_id,
								name: meta.value,
								price: restaurantProductMetaValue.price
							})
						})
					}
					const item = {
						name: productName,
						id: product.id,
						count: product.quantity,
						menu_id: product.menu_id,
						menu_type: menu.type,
						contains_alcohol: product.contains_alcohol
							? product.contains_alcohol == 1
							: 0,
						price: productPrice,
						img: product.item_img,
						restaurant_id: suborder.restaurant.id,
						comments: product.comments,
						options: options
					}
					let slot = deliverySlots[menu.id]
					if (!slot) {
						throw "Failed to find suitable delivery slot"
					}
					if (slot.time != -1) {
						items.push(
							new CartItemModel(item, slot.date, slot.time)
						)
					} else {
						items.push(new CartItemModel(item, slot.date))
					}
				})
				commit("batchAddToCart", {
					cartItems: items
				})
				return true
			} else {
				throw `API Error: ${response.status}`
			}
		} catch (e) {
			vue.bugsnag.notify("Failed to reorder order", (event) => {
				event.addMetadata("error", {
					error: e,
					order: order.id
				})
			})
			return false
		}
	},
	async addUserCreditCard({ commit, state }, payload) {
		try {
			const response = await api.addUserPaymentMethod(state.user.id, {
				card_nonce: payload.nonce,
				postal_code: payload.zipcode
			})
			if (response.status == 200) {
				commit("addedPaymentMethod", response.data.payment_method)
				return true
			} else {
				throw `API Error ${response.status}`
			}
		} catch (e) {
			vue.bugsnag.notify("Failed to add user credit card", (event) => {
				event.addMetadata("error", {
					error: e,
					userId: state.user.id
				})
			})
			throw e
		}
	},
	async setDefaultPaymentMethod({ commit, state }, paymentMethodId) {
		try {
			const response = await api.updatePaymentMethod(paymentMethodId, {
				payment_method: {
					is_default: 1
				}
			})
			if (response.status == 200) {
				commit("updatedPaymentMethod", {
					id: paymentMethodId,
					is_default: 1
				})
				return true
			} else {
				throw `API Error ${response.status}`
			}
		} catch (e) {
			vue.bugsnag.notify(
				"Failed to set default user credit card",
				(event) => {
					event.addMetadata("error", {
						message: e
					})
					event.addMetadata("data", {
						userId: state.user.id,
						methodId: paymentMethodId
					})
				}
			)
			throw e
		}
	},
	async deletePaymentMethod({ commit, state }, paymentMethodId) {
		try {
			const response = await api.deleteUserPaymentMethod(paymentMethodId)
			if (response.status == 200) {
				commit("deletedPaymentMethod", paymentMethodId)
				return true
			} else {
				throw `API Error ${response.status}`
			}
		} catch (e) {
			vue.bugsnag.notify("Failed to delete payment method", (event) => {
				event.addMetadata("error", {
					message: e
				})
				event.addMetadata("data", {
					userId: state.user.id,
					methodId: paymentMethodId
				})
			})
			throw e
		}
	},
	async shareReferral({ state }, payload) {
		const phones =
			payload && payload.phones
				? payload.phones.filter((phone) => phone.length > 0)
				: []
		const emails =
			payload && payload.emails
				? payload.emails.filter((email) => email.length > 0)
				: []
		try {
			let data = {}
			if (phones.length > 0) {
				data = {
					name: "user_referral_code_sms",
					attributes: {
						user_id: state.user.id,
						phone_numbers: phones
					}
				}
			} else if (emails.length > 0) {
				data = {
					name: "user_referral_code_email",
					attributes: {
						user_id: state.user.id,
						emails: emails
					}
				}
			}
			const response = await api.sendNotification(data)
			if (response.status == 200) {
				return true
			} else {
				throw `API Error ${response.status}`
			}
		} catch (e) {
			vue.bugsnag.notify("Failed to share referral code", (event) => {
				event.addMetadata("error", {
					message: e
				})
				event.addMetadata("data", {
					userId: state.user.id,
					phones: phones,
					emails: emails
				})
			})
			throw e
		}
	},
	markFlag({ commit }, payload) {
		if (payload.flag && payload.value) {
			commit("markedFlag", payload)
		}
	},
	async addBusinessEventReminder({ state }, eventId) {
		try {
			const response = await api.addBusinessEventReminder(eventId, {
				user_id: state.user.id
			})
			return response.status == 200
		} catch (e) {
			vue.bugsnag.notify(
				"Failed to add business event reminder",
				(event) => {
					event.addMetadata("error", {
						message: e
					})
					event.addMetadata("data", {
						eventId: eventId
					})
				}
			)
			return false
		}
	},
	validateDeliveryMode({ dispatch, getters, state }) {
		if (state.deliveryMode != null) {
			const deliveryModeAvailable = getters.cartDeliveryModes
				? getters.cartDeliveryModes.some(
						(cartDeliveryMode) =>
							cartDeliveryMode.deliveryMode == state.deliveryMode
				  )
				: false
			if (!deliveryModeAvailable) {
				dispatch("setDeliveryMode", null)
			} else if (state.businessDropoffLocationId != null) {
				if (
					state.deliveryMode != DeliveryMode.DIRECT_TO_LOCATION ||
					!getters.businessDropoffLocation
				) {
					dispatch("setDropoffLocationId", null)
				}
			}
		}
	},
	setDeliveryMode({ getters, commit, dispatch, state }, deliveryMode) {
		if (deliveryMode == null) {
			dispatch("sendMixpanelEvent", { msg: "Delivery mode reset" })
			commit("setDeliveryMode", null)
			dispatch("setDropoffLocationId", null)
		} else {
			const deliveryModeAvailable = getters.cartDeliveryModes
				? getters.cartDeliveryModes.some(
						(cartDeliveryMode) =>
							cartDeliveryMode.deliveryMode == deliveryMode
				  )
				: false
			if (deliveryModeAvailable) {
				dispatch("sendMixpanelEvent", { msg: "Delivery mode set" })
				commit("setDeliveryMode", deliveryMode)

				if (
					getters.isAirport &&
					deliveryMode != DeliveryMode.DIRECT_TO_LOCATION &&
					state.businessDropoffLocation != null
				) {
					dispatch("setDropoffLocationId", null)
				}
			}
		}
	},
	setDropoffLocationId({ commit, dispatch }, location) {
		if (location) {
			dispatch("sendMixpanelEvent", {
				msg: "Business Dropoff Location Set",
				data: {
					name: location.name,
					id: location.id
				}
			})
			commit("setDropoffLocationId", location.id)
		} else {
			dispatch("sendMixpanelEvent", {
				msg: "Business Dropoff Location Reset"
			})
			commit("setDropoffLocationId", null)
		}
	},
	setAccessibility({ commit, dispatch }, is_accessible_pickup) {
		dispatch("sendMixpanelEvent", {
			msg: "Accessibility Set",
			data: {
				is_accessible_pickup: is_accessible_pickup
			}
		})
		commit("setAccessibility", is_accessible_pickup)
	},
	async getMenuBrands({ commit, getters }) {
		try {
			const brandIds = getters.inStockMenus.reduce((ids, menu) => {
				if (menu.brand_id != null && !ids.includes(menu.brand_id)) {
					ids.push(menu.brand_id)
				}
				return ids
			}, [])
			if (brandIds.length > 0) {
				const response = await api.getBrands(brandIds.join())
				if (response.status == 200 && response.data.brands) {
					commit("gotMenuBrands", response.data.brands)
				} else {
					throw `API Error: ${response.status}`
				}
			}
			return true
		} catch (e) {
			vue.bugsnag.notify("Failed to get menu brands", {
				metaData: {
					error: e.msg
				}
			})
			return false
		}
	},
	shouldShowModal({ commit }, showModal) {
		commit("setModalToShow", showModal)
	}
}
